import type {SessionWritingState,WritingResponse,WritingResponseAck,WritingResponseWrite} from './api';

// Retain an uncertain exact body before sending any later edit. Receipts and
// per-task revisions distinguish retries from another tab's changed draft.
export class WritingResponseQueue {
 private rows=new Map<string,WritingResponse>();
 private pending=new Map<string,{content:string;state:'draft'|'submitted'}>();
 private uncertain:{id:string;body:WritingResponseWrite}|null=null;
 private running:Promise<boolean>|null=null;
 private listeners=new Set<()=>void>();
 private limits=new Map<string,number>();
 failed=false;
 conflict=false;
 constructor(state:SessionWritingState,private transport:(id:string,body:WritingResponseWrite)=>Promise<WritingResponseAck>,private isCurrent:()=>boolean){
  for(const task of state.tasks){this.limits.set(task.id,task.max_words);this.rows.set(task.id,state.responses.find(row=>row.task_id===task.id)||{task_id:task.id,revision:0,content:'',state:'draft'});}
 }
 subscribe(fn:()=>void){this.listeners.add(fn);return ()=>{this.listeners.delete(fn);};}
 private notify(){for(const fn of this.listeners)fn();}
 isPending(id:string){return this.pending.has(id)||this.uncertain?.id===id;}
 hasPending(){return Boolean(this.uncertain||this.pending.size);}
 response(id:string){const row=this.rows.get(id);if(!row)return undefined;const desired=this.pending.get(id)||(this.uncertain?.id===id?this.uncertain.body:null);return {...row,...(desired?{content:desired.content,state:desired.state}:{})};}
 stageDraft(id:string,content:string,state:'draft'|'submitted'='draft'){
  const row=this.response(id);
  if(!this.isCurrent()||this.conflict||!row||row.state==='submitted'||new TextEncoder().encode(content).length>64000||content.trim().split(/\s+/u).filter(Boolean).length>(this.limits.get(id)||0))return false;
  this.pending.set(id,{content,state});this.notify();return true;
 }
 save(id:string,content:string,state:'draft'|'submitted'='draft'){return this.stageDraft(id,content,state)?this.drain():Promise.resolve(false);}
 private drain():Promise<boolean>{
  if(this.running)return this.running;
  const work=async()=>{
   while(this.uncertain||this.pending.size){
    if(!this.isCurrent()||this.conflict)return false;
    if(!this.uncertain){const [id,next]=this.pending.entries().next().value as [string,{content:string;state:'draft'|'submitted'}];this.pending.delete(id);this.uncertain={id,body:{...next,expected_revision:this.rows.get(id)!.revision,request_id:crypto.randomUUID()}};}
    const current=this.uncertain;
    try{
     const ack=await this.transport(current.id,current.body);
     if(!this.isCurrent())return false;
     if(ack?.ok!==true||ack.task_id!==current.id||ack.request_id!==current.body.request_id||ack.revision!==current.body.expected_revision+1||ack.state!==current.body.state)throw new Error('Writing acknowledgement is invalid.');
     this.rows.set(current.id,{task_id:current.id,revision:ack.revision,content:current.body.content,state:ack.state});this.uncertain=null;
    }catch(error){this.failed=true;if([400,403,404,409].includes((error as {status?:number})?.status||0))this.conflict=true;this.notify();return false;}
   }
   this.failed=false;this.notify();return true;
  };
  this.running=work().finally(()=>{this.running=null;});return this.running;
 }
 async flush(){do{if(!await this.drain())throw new Error('Writing responses have not been acknowledged. Retry saving before completion.');}while(this.uncertain||this.pending.size);}
}
